/**
 * /matches/:matchId/scoring-diagnostic — admin scoring-diagnostic page.
 *
 * Spec: docs/superpowers/specs/2026-06-30-wants-has-scoring-design.md §8.1
 * (PR 3 of the wants→has scoring rollout).
 *
 * Backend contract: GET /v1/admin/matches/:matchId/scoring-diagnostic
 * (see `backend/src/routes/admin-matches-scoring-diagnostic.ts`).
 *
 * ── Layout ──────────────────────────────────────────────────────────────
 *
 *   ┌── Header ──────────────────────────────────────────────────────┐
 *   │ Match ID · matched_at · users · back-link                       │
 *   └────────────────────────────────────────────────────────────────┘
 *   ┌── Scores card ─────────────────────────────────────────────────┐
 *   │ Legacy vs new composite · delta · pillar breakdown · reverse    │
 *   └────────────────────────────────────────────────────────────────┘
 *   ┌── HF question rows table ─────────────────────────────────────┐
 *   │ For each HF Q: pillar tag, both users' wants + has            │
 *   └────────────────────────────────────────────────────────────────┘
 *
 * The intent is to answer "why did Manny see X" in <30s. The table
 * groups HF questions with PREF-pillar tag first so ops can eyeball
 * whether Manny's stated wants actually align with Irina's has values,
 * and if not, the numeric delta shows how much the new score is
 * penalizing the mismatch.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { useApiUrl } from '@refinedev/core';
import { adminFetch, AdminApiError } from '@/providers/admin-api';

// ─── Response types (matches the backend endpoint) ─────────────────────

interface UserRef {
  id: string;
  email: string | null;
  display_name: string | null;
}

interface StoredScoreRow {
  compatibility_score: number | null;
  dim_score_v: number | null;
  dim_score_l: number | null;
  pref_sat_a_to_b: number | null;
  pref_sat_b_to_a: number | null;
  sim_sat_score: number | null;
  wants_has_composite: number | null;
  wants_has_pref_qs_a: number | null;
  wants_has_pref_qs_b: number | null;
}

interface HfQuestionSide {
  wants: {
    selected_options: string[] | null;
    numeric_min: number | null;
    numeric_max: number | null;
    importance: number | null;
    is_dealbreaker: boolean | null;
    updated_at: string | null;
  } | null;
  has: {
    selected_options: string[] | null;
    numeric_value: number | null;
    numeric_min: number | null;
    numeric_max: number | null;
    updated_at: string | null;
  } | null;
}

interface HfQuestionDiagnosticRow {
  question_key: string;
  question_type: 'option' | 'numeric';
  pillar: 'PREF' | null;
  options: Array<{ key: string; label?: string; sort_order?: number }> | null;
  user1: HfQuestionSide;
  user2: HfQuestionSide;
}

interface DiagnosticResponse {
  match: {
    match_id: string;
    matched_at: string | null;
    status: string | null;
    user1: UserRef;
    user2: UserRef;
  };
  stored_scores: {
    forward: StoredScoreRow | null;
    reverse: StoredScoreRow | null;
    forward_delta: number | null;
  };
  hf_questions: HfQuestionDiagnosticRow[];
  meta: {
    wants_has_shadow_populated: boolean;
    pref_question_keys: string[];
    scoring_semantic: string;
  };
}

// ─── Small display helpers ─────────────────────────────────────────────

function fmtNum(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return String(n);
}

function fmtDelta(n: number | null): { text: string; className: string } {
  if (n === null) return { text: '—', className: 'text-muted-foreground' };
  if (n === 0) return { text: '0', className: 'text-muted-foreground' };
  if (n > 0) return { text: `+${n}`, className: 'text-emerald-600' };
  return { text: `${n}`, className: 'text-rose-600' };
}

/** Resolve an option `key` → display label using the question's options list. */
function labelFor(
  options: HfQuestionDiagnosticRow['options'],
  optKey: string,
): string {
  if (!options) return optKey;
  const found = options.find(o => o.key === optKey);
  return found?.label ?? optKey;
}

function fmtSelectedOptions(
  options: HfQuestionDiagnosticRow['options'],
  selected: string[] | null,
): string {
  if (!selected || selected.length === 0) return '—';
  return selected.map(k => labelFor(options, k)).join(', ');
}

function fmtNumericBand(
  min: number | null,
  max: number | null,
): string {
  if (min === null && max === null) return '—';
  if (min !== null && max !== null) return `${min} – ${max}`;
  if (min !== null) return `≥ ${min}`;
  return `≤ ${max}`;
}

// ─── Page ──────────────────────────────────────────────────────────────

export default function MatchScoringDiagnosticPage() {
  const { matchId } = useParams<{ matchId: string }>();
  const apiUrl = useApiUrl();
  const [data, setData] = useState<DiagnosticResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!matchId) return;
    setLoading(true);
    setErr(null);
    try {
      const r = await adminFetch(
        `${apiUrl}/v1/admin/matches/${matchId}/scoring-diagnostic`,
      );
      const json = (await r.json()) as DiagnosticResponse;
      setData(json);
    } catch (e: unknown) {
      setErr(e instanceof AdminApiError ? e.message : 'Failed to load diagnostic');
    } finally {
      setLoading(false);
    }
  }, [apiUrl, matchId]);

  useEffect(() => {
    reload();
  }, [reload]);

  if (!matchId) {
    return <div className="p-6 text-destructive">Missing match ID in URL.</div>;
  }

  if (loading && !data) {
    return (
      <div className="p-6 text-sm text-muted-foreground">
        Loading scoring diagnostic…
      </div>
    );
  }

  if (err && !data) {
    return (
      <div className="m-6 rounded-md bg-destructive/10 p-4 text-sm text-destructive">
        {err}
      </div>
    );
  }

  if (!data) return null;

  const { match, stored_scores, hf_questions, meta } = data;
  const forward = stored_scores.forward;
  const reverse = stored_scores.reverse;
  const deltaFmt = fmtDelta(stored_scores.forward_delta);

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <nav style={{ marginBottom: 12, fontSize: 13 }}>
        <Link
          to={`/matches/${matchId}`}
          className="inline-flex items-center gap-1 text-primary no-underline hover:underline"
        >
          <ChevronLeft size={14} /> Back to match detail
        </Link>
        <span className="mx-1.5 text-muted-foreground">/</span>
        <span className="text-muted-foreground">
          <code>{matchId}</code>
        </span>
        <span className="mx-1.5 text-muted-foreground">/</span>
        <span className="text-muted-foreground">scoring-diagnostic</span>
      </nav>

      {/* Header */}
      <div className="mb-4 rounded-lg border bg-card p-4 text-card-foreground">
        <h1 className="mb-2 text-lg font-semibold">Scoring diagnostic</h1>
        <div className="text-sm text-muted-foreground">
          {meta.scoring_semantic}
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3 text-[13px]">
          <div>
            <div className="text-muted-foreground">User 1 (A)</div>
            <div className="font-medium">
              {match.user1.display_name ?? match.user1.email ?? match.user1.id}
            </div>
            <div className="text-xs text-muted-foreground">{match.user1.email ?? ''}</div>
          </div>
          <div>
            <div className="text-muted-foreground">User 2 (B)</div>
            <div className="font-medium">
              {match.user2.display_name ?? match.user2.email ?? match.user2.id}
            </div>
            <div className="text-xs text-muted-foreground">{match.user2.email ?? ''}</div>
          </div>
        </div>
      </div>

      {/* Scores card */}
      <div className="mb-4 rounded-lg border bg-card p-4 text-card-foreground">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-semibold">Stored scores</h2>
          {!meta.wants_has_shadow_populated && (
            <span className="rounded-md bg-amber-500/10 px-2 py-0.5 text-xs text-amber-700 dark:text-amber-400">
              shadow not populated for this pair
            </span>
          )}
        </div>

        {/* Forward direction */}
        <div className="mb-4">
          <div className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">
            Forward (A → B)
          </div>
          <ScoreRow forward={forward} deltaFmt={deltaFmt} />
        </div>

        {reverse && (
          <div>
            <div className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">
              Reverse (B → A)
            </div>
            <ScoreRow forward={reverse} deltaFmt={fmtDelta(
              reverse.wants_has_composite !== null && reverse.compatibility_score !== null
                ? reverse.wants_has_composite - reverse.compatibility_score
                : null,
            )} />
          </div>
        )}
      </div>

      {/* HF question rows */}
      <div className="rounded-lg border bg-card text-card-foreground">
        <div className="border-b px-4 py-3">
          <h2 className="text-base font-semibold">Hard-filter questions</h2>
          <div className="text-xs text-muted-foreground">
            Rows tagged <span className="font-mono">PREF</span> feed the
            wants→has PREF pillar. Untagged rows (gender, sexual_orientation,
            age) are hard dealbreakers or scored elsewhere — spec §4.1.
          </div>
        </div>
        <table className="w-full text-[13px]">
          <thead>
            <tr className="border-b bg-muted/40 text-left">
              <th className="w-[130px] px-3 py-2">Question</th>
              <th className="w-[60px] px-3 py-2">Pillar</th>
              <th className="px-3 py-2">User 1 wants</th>
              <th className="px-3 py-2">User 1 has</th>
              <th className="px-3 py-2">User 2 wants</th>
              <th className="px-3 py-2">User 2 has</th>
            </tr>
          </thead>
          <tbody>
            {[...hf_questions]
              .sort((a, b) => {
                // Sort PREF rows first (they're the ones that carry
                // scoring signal), then alphabetically within a group.
                if (a.pillar !== b.pillar) {
                  return (a.pillar ? 0 : 1) - (b.pillar ? 0 : 1);
                }
                return a.question_key.localeCompare(b.question_key);
              })
              .map(q => (
                <QuestionRow key={q.question_key} q={q} />
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ScoreRow({
  forward,
  deltaFmt,
}: {
  forward: StoredScoreRow | null;
  deltaFmt: { text: string; className: string };
}) {
  if (!forward) {
    return (
      <div className="text-sm text-muted-foreground">
        No match_index row found (shadow-populate probably hasn't run for
        this direction yet).
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      <Metric
        label="Legacy composite"
        value={fmtNum(forward.compatibility_score)}
      />
      <Metric
        label="wants→has composite"
        value={fmtNum(forward.wants_has_composite)}
      />
      <Metric label="Δ (new − legacy)" value={deltaFmt.text} tint={deltaFmt.className} />
      <Metric
        label="SIM pillar"
        value={fmtNum(forward.sim_sat_score)}
      />
      <Metric
        label="PREF A→B"
        value={fmtNum(forward.pref_sat_a_to_b)}
        sub={`(${fmtNum(forward.wants_has_pref_qs_a)} Qs contributing)`}
      />
      <Metric
        label="PREF B→A"
        value={fmtNum(forward.pref_sat_b_to_a)}
        sub={`(${fmtNum(forward.wants_has_pref_qs_b)} Qs contributing)`}
      />
      <Metric label="Legacy V dim" value={fmtNum(forward.dim_score_v)} />
      <Metric label="Legacy L dim" value={fmtNum(forward.dim_score_l)} />
    </div>
  );
}

function Metric({
  label,
  value,
  sub,
  tint,
}: {
  label: string;
  value: string;
  sub?: string;
  tint?: string;
}) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`text-xl font-semibold tabular-nums ${tint ?? ''}`}>
        {value}
      </div>
      {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

function QuestionRow({ q }: { q: HfQuestionDiagnosticRow }) {
  const rowTint = q.pillar === 'PREF' ? '' : 'bg-muted/20';
  return (
    <tr className={`border-b last:border-b-0 ${rowTint}`}>
      <td className="px-3 py-2 font-mono text-xs">{q.question_key}</td>
      <td className="px-3 py-2">
        {q.pillar ? (
          <span className="rounded-md bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
            {q.pillar}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </td>
      <SideCells side={q.user1} question={q} />
      <SideCells side={q.user2} question={q} />
    </tr>
  );
}

function SideCells({
  side,
  question,
}: {
  side: HfQuestionSide;
  question: HfQuestionDiagnosticRow;
}) {
  return (
    <>
      <td className="px-3 py-2">
        {side.wants ? (
          <div>
            <div>
              {question.question_type === 'numeric'
                ? fmtNumericBand(side.wants.numeric_min, side.wants.numeric_max)
                : fmtSelectedOptions(question.options, side.wants.selected_options)}
            </div>
            <div className="text-xs text-muted-foreground">
              importance {fmtNum(side.wants.importance)}
              {side.wants.is_dealbreaker ? ' · dealbreaker' : ''}
            </div>
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">no want</span>
        )}
      </td>
      <td className="px-3 py-2">
        {side.has ? (
          <div>
            {question.question_type === 'numeric'
              ? fmtNum(side.has.numeric_value ?? side.has.numeric_min)
              : fmtSelectedOptions(question.options, side.has.selected_options)}
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">unanswered</span>
        )}
      </td>
    </>
  );
}
